import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { useLanguage } from '@/i18n/LanguageContext';
import { motion } from 'framer-motion';
import { HelpCircle, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { BadgeFAQContent } from '@/components/faq/BadgeFAQContent';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

interface FAQItem {
  question: string;
  answer: string;
  customContent?: boolean;
}

export default function FAQ() {
  const { t } = useLanguage();
  useDocumentHead({
    title: 'FAQ — Say It NOW Help Center',
    description: 'Answers about Living Announcements, JacKey AI, credits, privacy, and how Say It NOW helps you say it now.',
    path: '/faq',
  });

  const faqCategories = [
    {
      title: t.faq?.categoryGeneral || 'General Questions',
      items: [
        {
          question: t.faq?.q1 || 'What is Say It NOW?',
          answer: t.faq?.a1 || 'Say It NOW is a web platform that helps you create beautiful, magazine-style digital messages of love and gratitude to share with the people who matter most—while they can still see, hear, and appreciate them.'
        },
        {
          question: t.faq?.q2 || 'Why was Say It NOW created?',
          answer: t.faq?.a2 || 'Say It NOW was created because too often, we only hear how great and loved a person was after they\'ve passed away. People post heartfelt messages on social media to the deceased, but those words were never said while the person was alive. Say It NOW ensures that no one leaves this world without knowing how loved, appreciated, or great they are.'
        },
        {
          question: t.faq?.q3 || 'Who can I send a Living Announcement to?',
          answer: t.faq?.a3 || 'You can send a Living Announcement to anyone—parents, grandparents, siblings, children, spouses, friends, mentors, colleagues, or even yourself! If there\'s someone in your life who deserves to know how much they mean to you, Say It NOW makes it easy to tell them.'
        },
        {
          question: t.faq?.q4 || 'How is Say It NOW different from social media posts?',
          answer: t.faq?.a4 || 'Unlike social media posts that get lost in feeds, Say It NOW creates beautiful, personalized magazine-style announcements that feel special and intentional. They\'re designed to be treasured keepsakes, not fleeting posts. Plus, our AI features help you find the perfect words and remind you to stay connected.'
        }
      ] as FAQItem[]
    },
    {
      title: t.faq?.categoryFeatures || 'Features & Plans',
      items: [
        {
          question: t.faq?.q5 || 'What\'s included in the Free plan?',
          answer: t.faq?.a5 || 'The Free plan includes 5 announcements per month, the ability to upload 1-3 photos, select relationships, and access to our sentiment prompts and templates to help craft meaningful messages.'
        },
        {
          question: t.faq?.q6 || 'What AI features are available?',
          answer: t.faq?.a6 || 'Our Connection plan includes AI-powered message writing assistance to help you find the perfect words. Legacy users get AI Auto-Compose, which creates personalized announcements on your behalf and sends them on a schedule you set—perfect for staying connected even when life gets busy.'
        },
        {
          question: t.faq?.q7 || 'What are Heartfelt Reminders?',
          answer: t.faq?.a7 || 'Heartfelt Reminders are gentle nudges sent after 7 days of inactivity to encourage you to reconnect with your loved ones. Life gets busy, and these reminders help ensure you never forget to express your love. Available in Connection and Legacy plans.'
        },
        {
          question: t.faq?.q8 || 'How does AI Auto-Compose work?',
          answer: t.faq?.a8 || 'With AI Auto-Compose (Legacy feature), you set up your recipients with their relationship and any personal notes. Our AI crafts personalized, heartfelt messages based on your relationship. You get a 24-hour preview notification before any message is sent, giving you time to approve, edit, or skip it.'
        }
      ] as FAQItem[]
    },
    {
      title: t.faq?.categoryPrivacy || 'Privacy & Sharing',
      items: [
        {
          question: t.faq?.q9 || 'Is my data private and secure?',
          answer: t.faq?.a9 || 'Absolutely. Your Living Announcements are private by default. You control who sees them by sharing the unique link. We use industry-standard encryption and never share your personal information with third parties.'
        },
        {
          question: t.faq?.q10 || 'How do I share a Living Announcement?',
          answer: t.faq?.a10 || 'Once you create and publish your announcement, you\'ll receive a unique shareable link. You can send this link directly to your recipient via text, email, or any messaging platform. You can also share it on social media if you want others to see your beautiful message.'
        },
        {
          question: t.faq?.q11 || 'Can I edit or delete my announcements?',
          answer: t.faq?.a11 || 'Yes! You have full control over your announcements. You can edit them at any time from your dashboard, and you can delete them if needed. We believe your messages are yours to manage.'
        }
      ] as FAQItem[]
    },
    {
      title: t.faq?.categoryBadges || 'Badges, Points & Levels',
      items: [
        {
          question: t.faq?.q15 || 'How do badges work, and how do I earn them?',
          answer: '',
          customContent: true,
        }
      ] as (FAQItem & { customContent?: boolean })[]
    },
    {
      title: t.faq?.categoryBilling || 'Billing & Support',
      items: [
        {
          question: t.faq?.q12 || 'Do credits expire?',
          answer: t.faq?.a12 || 'No! Credits never expire. They remain in your account until you use them, so you can express love at your own pace without pressure.'
        },
        {
          question: t.faq?.q13 || 'Can I add more credits later?',
          answer: t.faq?.a13 || 'Absolutely. You can purchase additional credits or upgrade to a higher bundle anytime. Your existing credits carry over, and new features unlock as you grow with Say It NOW.'
        },
        {
          question: t.faq?.q14 || 'What if I need help?',
          answer: t.faq?.a14 || 'We\'re here to help! Legacy users get priority support. All users can reach out to us through our contact form, and we aim to respond within 24-48 hours. We\'re passionate about helping you express love to the people who matter most.'
        }
      ] as FAQItem[]
    }
  ];

  return (
    <div className="min-h-screen bg-hero-gradient">
      <Header />
      
      <main className="container py-16 md:py-24">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
            <HelpCircle className="h-4 w-4" />
            <span className="text-sm font-medium">{t.faq?.badge || 'Help Center'}</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
            {t.faq?.title || 'Frequently Asked Questions'}
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {t.faq?.subtitle || 'Everything you need to know about Say It NOW and how it helps you express love while it matters most.'}
          </p>
        </motion.div>

        {/* FAQ Categories */}
        <div className="max-w-4xl mx-auto space-y-12 mb-20">
          {faqCategories.map((category, categoryIndex) => (
            <motion.div
              key={categoryIndex}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: categoryIndex * 0.1 }}
            >
              <h2 className="font-heading text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-8 h-8 bg-primary text-primary-foreground rounded-full text-sm font-bold">
                  {categoryIndex + 1}
                </span>
                {category.title}
              </h2>
              
              <Accordion type="single" collapsible className="space-y-3">
                {category.items.map((item, itemIndex) => (
                  <AccordionItem
                    key={itemIndex}
                    value={`${categoryIndex}-${itemIndex}`}
                    className="glass border-0 rounded-xl px-6 data-[state=open]:shadow-soft transition-shadow"
                  >
                    <AccordionTrigger className="text-left font-medium text-foreground hover:no-underline py-5">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-5 leading-relaxed">
                      {item.customContent ? <BadgeFAQContent /> : item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          ))}
        </div>

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center bg-gradient-to-br from-primary/10 via-background to-primary/5 rounded-3xl p-12"
        >
          <MessageCircle className="h-12 w-12 text-primary mx-auto mb-6" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t.faq?.contactTitle || 'Still Have Questions?'}
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
            {t.faq?.contactSubtitle || 'We\'re here to help you on your journey to expressing love. Reach out anytime!'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                {t.faq?.contactButton || 'Get Started Free'}
              </Button>
            </Link>
            <a href="mailto:support@say-it-now.app?subject=Support%20Request">
              <Button size="lg" variant="outline">
                {t.faq?.contactEmail || 'Contact Support'}
              </Button>
            </a>
          </div>
        </motion.div>
      </main>

      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqCategories
              .flatMap((cat) => cat.items)
              .filter((item) => !item.customContent && item.answer)
              .map((item) => ({
                '@type': 'Question',
                name: item.question,
                acceptedAnswer: { '@type': 'Answer', text: item.answer },
              })),
          }),
        }}
      />
    </div>
  );
}
